import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import isEqual from 'react-fast-compare';
import {useSelector} from 'react-redux';
import {debounce} from 'ts-debounce';

import BenchlingConfig from 'BenchlingConfig';
import * as metrics from 'metrics/Metrics';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import * as Perf from 'performance/Perf';
import {BenchlingState} from 'redux-stores/reduceApp';
import CtrlFView from 'ui-library/CtrlFView';
import useIsNotUnmounted from 'ui-library/hooks/useIsNotUnmounted';
import APIUtil from 'util/APIUtil';
import {buildIndexTree} from 'util/IndexTree';

import {Editor} from '../EditorTypes';
import {jumpToResult, useCtrlFKeydown} from './DOM';
import {MediocreSearchResultProps} from './SearchResultProps';
import getSearchResults, {MediocreSearchResult, getSearchResultIndex} from './SearchResults';

export interface MediocreSearchState extends MediocreSearchResultProps {
  selectedIndex: number;
  searchResults: Array<MediocreSearchResult>;
  isTruncated: boolean;
  isLoading: boolean;
}

export const NO_MATCHES: MediocreSearchState = {
  selectedIndex: -1,
  searchResults: [],
  searchResultsTree: null,
  selectedSearchResultTree: null,
  isTruncated: false,
  isLoading: false,
};

const LOADING: MediocreSearchState = {...NO_MATCHES, isLoading: true};

function getSearchStateFromNewResults(
  newSearchResults: Array<MediocreSearchResult>,
  isTruncated: boolean
): MediocreSearchState {
  return {
    selectedIndex: -1,
    searchResults: newSearchResults,
    // The following state is effectively a memoization of an expensive derivation from the above state.
    // Typically we could expose this with a hook in the parent that utilizes useMemo, but our parent
    // Editor component is still a class-based component. Until then, we'll use this slight anti-pattern as
    // separation of concerns is more helpful here.
    searchResultsTree: buildIndexTree(newSearchResults, getSearchResultIndex),
    selectedSearchResultTree: null,
    isTruncated,
    isLoading: false,
  };
}

function useLastValueRef<T>(value: T): React.MutableRefObject<T> {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

function useDebouncedSearch<TEditor extends Editor<TRoot, TItem>, TRoot, TItem>(
  editor: TEditor,
  setSearchStateRef: React.MutableRefObject<(searchState: MediocreSearchState) => void>,
  debounceMs: number,
  searchOverFormulas: boolean
): (
  doc: TRoot,
  items: Array<TItem>,
  focusedItem: TItem | null,
  query: string,
  isTableCollapsedById: {[cid: string]: boolean | undefined}
) => void {
  const isMountedRef = useIsNotUnmounted();
  return useMemo(
    () =>
      debounce(
        (
          doc: TRoot,
          items: Array<TItem>,
          focusedItem: TItem | null,
          query: string,
          isTableCollapsedById: {[cid: string]: boolean}
        ) => {
          if (!isMountedRef.current || !query) {
            return;
          }
          const queryLength = query.length;
          let resultCount = -1;
          const fileType = APIUtil.apiIdToType(editor.props.fileId);
          const docRowCount = getDocumentRowCountForAnalytics(editor, doc);
          const perfId =
            Perf.start('notebook_doc_search', {
              enabled: true,
              getMetadata: () => ({queryLength, resultCount, fileType, docRowCount}),
            }) ?? '';
          Perf.markStart(perfId);
          const resultsGenerator = getSearchResults(
            editor,
            doc,
            items,
            focusedItem,
            query,
            searchOverFormulas,
            isTableCollapsedById
          );
          const {results, isTruncated} = getSearchResultsWithTruncation(resultsGenerator);
          resultCount = results.length;
          metrics.distribution('bnch.notebook.search.numResults', resultCount, {isTruncated});
          Perf.markEnd(perfId);
          Perf.stop(perfId);
          const newSearchState = getSearchStateFromNewResults(results, isTruncated);
          setSearchStateRef.current(newSearchState);
        },
        debounceMs
      ),
    [editor, setSearchStateRef, debounceMs, isMountedRef, searchOverFormulas]
  );
}

/** Calculate table rows in document to monitor how table size in docs affects search performance. */
function getDocumentRowCountForAnalytics<TEditor extends Editor<TRoot, TItem>, TRoot, TItem>(
  editor: TEditor,
  doc: TRoot
): number {
  const tableMap = editor.getComputedTables(doc);
  if (tableMap == null) {
    return 0;
  }
  return Object.values(tableMap).reduce<number>((acc, table) => acc + table.length, 0);
}

/**
 * Iterates the generator returned by getSearchResults and applies an optional max number of results based
 * on the value of ELN_VIRTUALIZED_SEARCH_MAX_RESULTS.
 *
 * @param it a generator the produces search results for the current search.
 * @returns the results and a flag indicating if there are more results than those returned.
 */
function getSearchResultsWithTruncation(it: Generator<MediocreSearchResult>): {
  results: Array<MediocreSearchResult>;
  isTruncated: boolean;
} {
  if (BenchlingConfig.ELN_VIRTUALIZED_SEARCH_MAX_RESULTS <= -1) {
    return {results: Array.from(it), isTruncated: false};
  }

  const results: Array<MediocreSearchResult> = [];
  for (const result of it) {
    if (results.length === BenchlingConfig.ELN_VIRTUALIZED_SEARCH_MAX_RESULTS) {
      return {results, isTruncated: true};
    }
    results.push(result);
  }

  return {results, isTruncated: false};
}

/**
 * When `setSearchState` is called, `editor` must immediately re-render with updated searchState where a
 * selected search result must not be virtualized.
 */
export default function useCtrlF<TEditor extends Editor<TRoot, TItem>, TRoot, TItem>(
  editor: TEditor,
  doc: TRoot,
  searchableItems: Array<TItem>,
  focusedItem: TItem | null,
  searchState: MediocreSearchState = NO_MATCHES,
  setSearchState: (searchState: MediocreSearchState) => void,
  debounceMs: number,
  isOpen: boolean,
  setOpen: (isOpen: boolean) => void,
  query: string,
  setQuery: (query: string) => void
): {
  ref: React.RefObject<CtrlFView>;
  isLoading: boolean;
  isTruncated: boolean;
  selectedIndex: number;
  setSelectedIdx: (index: number) => void;
  searchResults: Array<MediocreSearchResult>;
  doClose: () => void;
} {
  const {searchResults, isLoading, isTruncated, selectedIndex} = searchState;
  const {showFormulas} = EditorPropertiesContext.useContext();

  const ref = useRef<CtrlFView>(null);
  const onCtrlF = useCallback(
    (newQuery: string | undefined) => {
      setOpen(true);
      if (newQuery) {
        setQuery(newQuery);
      }
      ref.current?.focusAndSelect();
    },
    [setOpen, setQuery]
  );

  const doClose = useCallback(() => {
    setOpen(false);
    setSearchState(NO_MATCHES);
  }, [setSearchState, setOpen]);

  // setSearchState should not trigger our useEffect hooks since they trigger when state updates.
  const setSearchStateRef = useLastValueRef(setSearchState);

  // Debounce to avoid performance hit from repeated searching while typing.
  const doDebouncedSearch = useDebouncedSearch(editor, setSearchStateRef, debounceMs, showFormulas);

  const collapsedSelector = useCallback(
    (state: BenchlingState) => {
      const collapsedState = state.ui.collapsedTables;
      if (!isOpen) {
        return {};
      }
      return editor.props
        .getItems(doc)
        .reduce((isTableCollapsedById: {[cid: string]: boolean | undefined}, item) => {
          const type = editor.props.getType(item);
          const plugin = editor.getPluginByType(type);
          const cid = editor.props.getCid(item);
          const isCollapsed = plugin.component.getIsCollapsed?.(
            item,
            editor.state.editorProps,
            collapsedState
          );
          isTableCollapsedById[cid] = isCollapsed;
          return isTableCollapsedById;
        }, {});
    },
    [isOpen, editor, doc]
  );
  const isTableCollapsedById = useSelector(collapsedSelector, isEqual);
  const prevDepsRef = useRef({});

  // If the note or query has changed, we want to do a search again.
  useEffect(() => {
    const prevDeps = prevDepsRef.current;
    const newDeps = {
      doc,
      query,
      isTableCollapsedById,
      isOpen,
      searchableItems,
      focusedItem,
      setSearchStateRef,
      doDebouncedSearch,
    };

    // Checking for deep equality of previous dependencies and new dependencies to avoid counting as not equal
    // due to change in object refs. If they're deeply equal, end useEffect before triggering search.
    // This is primarily necessary because isTableCollapsedById causes this useEffect to trigger frequently as its
    // ref changes which caused buggy behavior with the ctrlf search re-running unexpectedly, resulting in bad
    // user experience.
    if (isEqual(prevDeps, newDeps)) {
      return;
    }

    prevDepsRef.current = newDeps;

    if (!isOpen) {
      return;
    }
    setSearchStateRef.current(query ? LOADING : NO_MATCHES);
    doDebouncedSearch(doc, searchableItems, focusedItem, query, isTableCollapsedById);
  }, [
    doc,
    query,
    isTableCollapsedById,
    isOpen,
    searchableItems,
    setSearchStateRef,
    doDebouncedSearch,
    focusedItem,
  ]);

  const setSelectedIdx = useCallback(
    (newSelectedIndex: number): void => {
      if (searchResults.length === 0) {
        return;
      }
      const modulus = searchResults.length;
      const normalizedIndex = ((newSelectedIndex % modulus) + modulus) % modulus;

      const newSelectedResult = searchResults[normalizedIndex];
      const newSelectedSearchResultTree = buildIndexTree([newSelectedResult], getSearchResultIndex);
      setSearchStateRef.current({
        ...searchState,
        selectedIndex: normalizedIndex,
        selectedSearchResultTree: newSelectedSearchResultTree,
      });
      // After setSearchState is called, we wait one render tick to wait for the Editor to render the result
      // before scrolling the result into view.
      setTimeout(() => {
        jumpToResult(newSelectedResult);
      }, 0);
    },
    [searchResults, searchState, setSearchStateRef]
  );

  const onCtrlG = useCallback(
    (reverse: boolean) => {
      if (!isOpen) {
        setOpen(true);
        ref.current?.focusAndSelect();
        return;
      }

      setSelectedIdx(reverse ? selectedIndex - 1 : selectedIndex + 1);
    },
    [isOpen, setOpen, selectedIndex, setSelectedIdx]
  );

  useCtrlFKeydown(onCtrlF, onCtrlG, doClose);

  return {
    ref,
    isLoading,
    isTruncated,
    selectedIndex,
    setSelectedIdx,
    searchResults,
    doClose,
  };
}
